import { FavoritesMap } from 'app/classes/_lib/favorites/getFavoriteIdsByType';
import { getIDFromSlug } from 'lib/api/utils';
import { BadgeVariant } from 'components/classes/Badge/Badge';
import {
  Class,
  Media,
  Instructor,
  ParentLesson,
  TrackedLesson,
  RecipeLesson,
  ArticleLesson,
} from 'types/classes';
import { formatDuration } from 'app/lessons/_utils/lessonHelpers';
import {
  FavoriteEntity,
  FavoriteEntityWithType,
} from 'app/classes/_lib/getDataForFavorites';
import { DocumentType } from 'app/classes/_lib/favorites/documentType.type';
import {
  DocumentType as EnumDocumentType,
  InteractionType,
  Location,
} from 'enums';

export const getInstructorNames = (classItem: Class): string => {
  const instructors = classItem.attributes.instructors?.data || [];

  const getNames = (instructors: Instructor[]) => {
    return instructors.map(
      (instructor) =>
        `${instructor.attributes.first_name} ${instructor.attributes.last_name}`,
    );
  };

  const names = getNames(instructors);

  return names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

export const getCloudinaryId = (classItem: Class): string | undefined => {
  return classItem.attributes.hero_image?.data?.attributes?.provider_metadata
    ?.public_id;
};

export const isNewClass = (classItem: Class): boolean => {
  const publishedAt = classItem.attributes.publishedAt;
  if (!publishedAt) return false;

  const daysDifference = Math.floor(
    (Date.now() - new Date(publishedAt).getTime()) / (1000 * 60 * 60 * 24),
  );

  return daysDifference < 30;
};

export const getBadgeVariant = (
  classItem: Class,
  progress?: number,
): BadgeVariant | undefined => {
  if (progress === 100) return 'completed';
  if (isNewClass(classItem)) return 'new';
  return undefined;
};

export const isFavoriteClass = (
  classId: number,
  favoritesMap: FavoritesMap,
): boolean => {
  return favoritesMap.some((fav) => fav.documentId === classId);
};

export const mapClassToCardProps = (
  classItem: Class,
  options: {
    progress?: number;
    isFavorite: boolean;
    onClickFavorite: () => Promise<void>;
    onClickLink?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  },
) => {
  const { progress, isFavorite, onClickFavorite, onClickLink } = options;
  const { headline, slug, short_dek } = classItem.attributes;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.target as HTMLElement;
    const isFavoriteClick = target.closest('button') !== null;

    if (!isFavoriteClick && onClickLink) {
      onClickLink(e);
    }
  };
  const classSlug = slug !== SLUG_DEFAULT ? slug : '';

  return {
    title: {
      text: headline,
    },
    author: getInstructorNames(classItem),
    description: short_dek,
    cloudinaryId: getCloudinaryId(classItem),
    link: {
      href: `/classes/${
        classSlug
          ? `${classItem.id}-${classSlug}`
          : makeSlug(classItem.id, headline)
      }`,
      onClick: options?.onClickLink ? handleLinkClick : undefined,
    },
    progress,
    badgeVariant: getBadgeVariant(classItem, progress),
    isFavorite,
    onClickFavorite,
  };
};

export const getLessonTypeNameForLink = (
  a?: ParentLesson['attributes'] | null,
): string =>
  a?.article_lesson?.data
    ? 'articles'
    : a?.video_lesson?.data
    ? 'videos'
    : a?.recipe_lesson?.data
    ? 'recipes'
    : '';

export const mapLessonTypeToDocumentType = (
  a?: ParentLesson['attributes'] | null,
): string =>
  a?.article_lesson?.data
    ? 'articleLesson'
    : a?.video_lesson?.data
    ? 'videoLesson'
    : a?.recipe_lesson?.data
    ? 'recipeLesson'
    : '';

export const getLessonTypeNameFromDocumentType = (
  documentType: DocumentType,
): string =>
  documentType === 'class'
    ? 'classes'
    : documentType === 'articleLesson'
    ? 'lessons/articles'
    : documentType === 'videoLesson'
    ? 'lessons/videos'
    : documentType === 'recipeLesson'
    ? 'lessons/recipes'
    : '';

export const getLinkFromDocumentType = (
  documentId: number | string,
  documentType: DocumentType,
): string =>
  `/${getLessonTypeNameFromDocumentType(documentType)}/${documentId}`;

export const isLessonCompleted = (
  completedLessons?: TrackedLesson[],
  id?: number | string,
  documentType?: string,
  classId?: string,
) => {
  return completedLessons?.some(
    (lesson) =>
      lesson.document_id === id?.toString() &&
      lesson.document_type === documentType &&
      lesson.class_id === classId,
  );
};

export const SLUG_DEFAULT = 'this-will-be-auto-generated';
export const getLessonLink = (
  id: number,
  lessonTypeName: string,
  lessonName: string,
  lessonSlug?: string,
  classSlug?: string,
): string =>
  `/lessons/${lessonTypeName}/${
    lessonSlug ? `${id}-${lessonSlug}` : makeSlug(id, lessonName)
  }${classSlug ? `?classId=${classSlug}` : ''}`;

export const mapLessonToCardProps = (
  attributes: ParentLesson['attributes'],
  classSlug?: string,
  completedLessons?: TrackedLesson[],
  options?: { onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void },
) => {
  const { article_lesson, recipe_lesson, video_lesson } = attributes ?? {};
  const realLesson =
    article_lesson?.data || recipe_lesson?.data || video_lesson?.data;
  const image =
    (realLesson?.attributes as { image?: { data: Media } })?.image?.data ??
    (realLesson?.attributes as { images?: { data: Media[] } })?.images
      ?.data?.[0];

  const lessonDocumentType = mapLessonTypeToDocumentType(attributes);

  const isCompleted = isLessonCompleted(
    completedLessons,
    realLesson?.id,
    lessonDocumentType,
    classSlug ? getIDFromSlug(classSlug) : undefined,
  );

  const title = recipe_lesson?.data
    ? (recipe_lesson.data.attributes as RecipeLesson['attributes']).displayName
    : attributes?.title;

  const duration = recipe_lesson?.data
    ? (realLesson?.attributes as RecipeLesson['attributes']).totalTime
    : (realLesson?.attributes as ArticleLesson['attributes']).timeEST;

  const description = formatDuration(
    lessonDocumentType as DocumentType,
    duration,
  );
  return {
    title: {
      text: title,
    },
    link: {
      href: getLessonLink(
        realLesson?.id ?? 0,
        getLessonTypeNameForLink(attributes),
        title,
        realLesson?.attributes.slug !== SLUG_DEFAULT
          ? realLesson?.attributes.slug
          : '',
        classSlug,
      ),
      onClick: options?.onClick,
    },
    cloudinaryId: image?.attributes?.provider_metadata?.public_id ?? '',
    description,
    hasPlayIcon: Boolean(realLesson?.attributes.zypeId),
    badgeVariant: realLesson?.attributes.tag?.data.attributes
      .tag_name as BadgeVariant,
    variant: (isCompleted ? 'completed' : 'usual') as 'usual' | 'completed',
  };
};

const getImagePublicId = (imageData: any): string | undefined => {
  return imageData?.data?.attributes?.provider_metadata?.public_id;
};

export const mapFavoriteToCardProps = (
  favorite: FavoriteEntity,
  documentType: DocumentType,
  options?: {
    showBadge?: boolean;
    onClickLink?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  },
) => {
  const isClass = documentType === 'class';

  const attrs = favorite.attributes;
  const image = isClass
    ? getImagePublicId((attrs as Class['attributes']).hero_image)
    : documentType === 'recipeLesson'
    ? (attrs as RecipeLesson['attributes']).images?.data?.[0]?.attributes
        ?.provider_metadata?.public_id
    : getImagePublicId((attrs as ArticleLesson['attributes']).image);

  const title = isClass
    ? (attrs as Class['attributes']).headline
    : documentType === 'recipeLesson'
    ? (attrs as RecipeLesson['attributes']).displayName
    : (attrs as ArticleLesson['attributes']).title;

  const badgeVariant = (
    options?.showBadge
      ? isClass
        ? 'class'
        : (attrs as ArticleLesson['attributes']).tag?.data?.attributes?.tag_name
      : undefined
  ) as BadgeVariant | undefined;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.target as HTMLElement;
    const isFavoriteClick = target.closest('button') !== null;

    if (!isFavoriteClick && options?.onClickLink) {
      options.onClickLink(e);
    }
  };

  const cardLinkSlug =
    attrs.slug && attrs.slug !== SLUG_DEFAULT
      ? `${favorite.id}-${attrs.slug}`
      : makeSlug(favorite.id, title);

  return {
    title: { text: title },
    link: {
      href: getLinkFromDocumentType(cardLinkSlug, documentType),
      onClick: options?.onClickLink ? handleLinkClick : undefined,
    },
    cloudinaryId: image ?? '',
    author: isClass ? getInstructorNames(favorite as Class) : undefined,
    isFavorite: true,
    badgeVariant,
  };
};

export const mapClassToStickyFooter = (
  classItem: Class,
  completedLessons: TrackedLesson[],
) => {
  const allLessons: ParentLesson[] = [];
  classItem.attributes.modules.data.forEach((module) => {
    module.attributes.parent_lessons.data.forEach((lesson) => {
      allLessons.push(lesson);
    });
  });

  const variant =
    completedLessons.length === 0
      ? 'new'
      : completedLessons.length < allLessons.length
      ? 'continues'
      : 'completed';

  const getFirstUncompletedLesson = (lessons: ParentLesson[]) => {
    for (const lesson of lessons) {
      const { article_lesson, recipe_lesson, video_lesson } = lesson.attributes;
      const realLesson =
        article_lesson?.data || recipe_lesson?.data || video_lesson?.data;
      const title = recipe_lesson?.data
        ? (realLesson?.attributes as RecipeLesson['attributes']).displayName
        : (realLesson?.attributes as ArticleLesson['attributes']).title;
      const documentType = mapLessonTypeToDocumentType(lesson.attributes);
      const isCompleted = completedLessons.some(
        (tracked) =>
          tracked.document_id === realLesson?.id?.toString() &&
          tracked.document_type === documentType,
      );
      if (!isCompleted && realLesson) {
        return {
          url: getLessonLink(
            realLesson.id,
            getLessonTypeNameForLink(lesson.attributes),
            title,
            realLesson?.attributes.slug !== SLUG_DEFAULT
              ? realLesson?.attributes.slug
              : '',
            classItem.attributes.slug &&
              classItem.attributes.slug !== SLUG_DEFAULT
              ? `${classItem.id}-${classItem.attributes.slug}`
              : makeSlug(classItem.id, classItem.attributes.headline),
          ),
          analyticsProps: getLessonAnalyticsProps(lesson, classItem),
        };
      }
    }
    return undefined;
  };

  const firstUncompletedLesson = getFirstUncompletedLesson(allLessons);

  return {
    title: classItem.attributes.headline,
    level: 'class' as const,
    variant: variant as 'new' | 'continues' | 'completed',
    nextLessonUrl: firstUncompletedLesson?.url,
    analyticsProps: firstUncompletedLesson?.analyticsProps,
  };
};

export const getAnalyticsPropsForClassCard = (
  classItem: Class,
  location: Location,
  position?: number,
  parentLabel?: string,
) => ({
  document_type: EnumDocumentType.CLASS,
  document_id: classItem.id,
  document_title: classItem.attributes.headline,
  class_id: classItem.id,
  class_title: classItem.attributes.headline,
  parentlabel: parentLabel,
  location,
  position,
});

export const getAnalyticsPropsForFavoriteCard = (
  favorite: FavoriteEntityWithType,
  location: Location,
  position?: number,
  parentLabel?: string,
) => ({
  document_type: favorite.documentType,
  document_id: favorite.documentId,
  document_title:
    (favorite.entity as Class).attributes.headline ||
    (favorite.entity as ArticleLesson).attributes.title ||
    (favorite.entity as RecipeLesson).attributes.displayName,
  class_id: favorite.documentType === 'class' ? favorite.documentId : undefined,
  class_title:
    favorite.documentType === 'class'
      ? (favorite.entity as Class).attributes.headline
      : undefined,
  lesson_standalone: favorite.documentType !== 'class' ? true : undefined,
  parentlabel: parentLabel,
  location,
  position,
});

export const getAnalyticsPropsForLessonCard = (
  lesson: ParentLesson,
  location: Location,
  classItem?: Class,
  interactionType?: InteractionType,
) => {
  const documentType = mapLessonTypeToDocumentType(lesson.attributes);
  const { recipe_lesson, article_lesson, video_lesson } = lesson.attributes;

  const title =
    recipe_lesson?.data?.attributes?.displayName ??
    article_lesson?.data?.attributes?.title ??
    video_lesson?.data?.attributes?.title;

  const id =
    recipe_lesson?.data?.id ??
    article_lesson?.data?.id ??
    video_lesson?.data?.id;

  return {
    interaction_type: interactionType,
    document_type: documentType,
    document_id: id,
    document_title: title,
    class_id: classItem?.id,
    class_title: classItem?.attributes.headline,
    lesson_standalone: !classItem?.id,
    location,
  };
};

export const getClassAnalyticsProps = (classItem: Class) => ({
  document_type: EnumDocumentType.CLASS,
  document_id: classItem.id,
  document_title: classItem.attributes.headline,
  class_id: classItem.id,
  class_title: classItem.attributes.headline,
});

export const getLessonAnalyticsProps = (
  lesson: ParentLesson,
  classItem: Class,
) => {
  const { article_lesson, recipe_lesson, video_lesson } = lesson.attributes;
  const realLesson =
    recipe_lesson?.data || article_lesson?.data || video_lesson?.data;

  return {
    document_type: mapLessonTypeToDocumentType(lesson.attributes),
    document_id: realLesson?.id,
    document_title:
      recipe_lesson?.data?.attributes?.displayName ??
      article_lesson?.data?.attributes?.title ??
      video_lesson?.data?.attributes?.title,
    lesson_standalone: false,
    class_id: classItem.id,
    class_title: classItem.attributes.headline,
  };
};

export function makeSlug(id: number | string, name: string): string {
  const words = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .split(/[^a-z0-9]+/) // split by non-alphanumeric
    .filter(Boolean); // remove empty parts

  return [String(id), ...words].join('-');
}
